"use client";

import React, { useState, useEffect } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import CardHeader from './CardHeader';
import CircularProgressChart from './CircularProgressChart';
import { metricsService, SopDeviationSummary } from '../../../services/metricsService';

interface SOPDeviationCardProps {
  businessIdentifier?: string;
  timeRange?: string;
  value?: number;
  trend?: string;
  trendValue?: string;
  trendDirection?: 'up' | 'down';
}

const SOPDeviationCard: React.FC<SOPDeviationCardProps> = ({
  businessIdentifier,
  timeRange = '7d',
  value: propValue,
  trend: propTrend,
  trendValue: propTrendValue,
  trendDirection: propTrendDirection
}) => {
  const [sopData, setSopData] = useState<SopDeviationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSopData = async () => {
      // Always try to fetch from API first, use props as fallback
      setIsLoading(true);
      setError(null);
      try {
        const data = await metricsService.getSopDeviationSummary(businessIdentifier, timeRange);
        setSopData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load SOP deviation data.');
        console.error('Error fetching SOP data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSopData();
  }, [businessIdentifier, timeRange]);

  // Use API data if available, otherwise use prop values, otherwise use defaults
  // const value = sopData ? (100 - sopData.compliance_percentage) : (propValue !== undefined ? propValue : 62.4);
  const value = sopData && sopData.compliance_percentage != null 
    ? sopData.compliance_percentage.toFixed(2) 
    : "0.00";
  
  // Fix trend logic: use API data if sopData exists and has trend property (including 0)
  const trend = sopData && sopData.trend !== undefined ? 
    (sopData.trend === 0 ? 'No change' : sopData.trend > 0 ? 'Up by' : 'Down by') : 
    (propTrend || 'Up by');
    
  const trendValue = sopData && sopData.trend !== undefined ? 
    (sopData.trend === 0 ? '0%' : `${Math.abs(sopData.trend)}%`) : 
    (propTrendValue || '3%');
    
  const trendDirection: 'up' | 'down' = sopData && sopData.trend !== undefined ? 
    (sopData.trend >= 0 ? 'up' : 'down') : 
    (propTrendDirection || 'up');

  // Position the green indicator at the bottom for SOP Deviation
  const secondaryColor = trendDirection === 'up' ? '#79FFB5' : '#FF7979';

  if (isLoading) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: "3", md: "4" }}
        h={{ base: "auto", md: "350px" }}
        minH={{ base: "335px", md: "auto" }}
        w={{ base: "full", md: "370px" }}
        maxW={{ base: "370px", md: "full" }}
        mx={{ base: "auto", md: "0" }}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: "3", md: "4" }}
        h={{ base: "auto", md: "350px" }}
        minH={{ base: "335px", md: "auto" }}
        w={{ base: "full", md: "370px" }}
        maxW={{ base: "370px", md: "full" }}
        mx={{ base: "auto", md: "0" }}
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="red.500" fontSize="sm" textAlign="center">{error}</Text>
      </Box>
    );
  }
  
  return (
    <Box
      bg="white"
      borderRadius="xl"
      shadow="sm"
      p={{ base: "3", md: "4" }}
      h={{ base: "auto", md: "350px" }}
      minH={{ base: "335px", md: "auto" }}
      w={{ base: "full", md: "370px" }}
      maxW={{ base: "370px", md: "full" }}
      mx={{ base: "auto", md: "0" }}
      position="relative"
    >
      <CardHeader 
        title="SOP Deviation" 
        trend={trend} 
        trendValue={trendValue} 
        trendDirection={trendDirection} 
      />
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        h="64" 
        mt={{ base: "5", md: "0" }}
      >
        <CircularProgressChart 
          value={value} 
          primaryColor="#7987FF" 
          secondaryColor={secondaryColor} 
          trendValue={trendValue}
          trendDirection={trendDirection}
        />
      </Box>
    </Box>
  );
};

export default SOPDeviationCard; 
